import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import * as React from 'react';

import {
  applyEditorLink,
  type EditorSelectionRange,
} from '../../lib/apply-editor-link';
import type { ShouldShowProps } from '../../types';
import { LinkEditBlock } from '../link/link-edit-block';
import { LinkPopoverBlock } from '../link/link-popover-block';

interface LinkBubbleMenuProps {
  editor: Editor;
}

interface LinkAttributes {
  href: string;
  target: string;
}

export const LinkBubbleMenu: React.FC<LinkBubbleMenuProps> = ({ editor }) => {
  const [showEdit, setShowEdit] = React.useState(false);
  const savedSelection = React.useRef<EditorSelectionRange | null>(null);
  const [linkAttrs, setLinkAttrs] = React.useState<LinkAttributes>({
    href: '',
    target: '',
  });
  const [selectedText, setSelectedText] = React.useState('');

  const updateLinkState = React.useCallback(() => {
    const { from, to } = editor.state.selection;
    const { href = '', target = '' }: Partial<LinkAttributes> =
      editor.getAttributes('link');
    const text = editor.state.doc.textBetween(from, to, ' ');

    setLinkAttrs({ href, target });
    setSelectedText(text);
  }, [editor]);

  const shouldShow = React.useCallback(
    ({ editor, from, to }: ShouldShowProps) => {
      if (showEdit) {
        return true;
      }

      if (from === to) {
        return false;
      }

      const { href } = editor.getAttributes('link');

      if (href) {
        savedSelection.current = { from, to };
        updateLinkState();
        return true;
      }

      return false;
    },
    [showEdit, updateLinkState],
  );

  const handleEdit = React.useCallback(() => {
    const { from, to } = editor.state.selection;
    savedSelection.current = { from, to };
    updateLinkState();
    setShowEdit(true);
  }, [editor, updateLinkState]);

  const onSetLink = React.useCallback(
    (url: string, text?: string, openInNewTab?: boolean) => {
      applyEditorLink(editor, url, {
        text,
        openInNewTab,
        selection: savedSelection.current ?? undefined,
      });
      setShowEdit(false);
      updateLinkState();
    },
    [editor, updateLinkState],
  );

  const onUnsetLink = React.useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowEdit(false);
    savedSelection.current = null;
    updateLinkState();
  }, [editor, updateLinkState]);

  const stopMenuPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      options={{
        placement: 'bottom-start',
        onHide: () => {
          setShowEdit(false);
          savedSelection.current = null;
        },
      }}
    >
      <div onPointerDown={stopMenuPointerDown}>
        {showEdit ? (
          <LinkEditBlock
            defaultUrl={linkAttrs.href}
            defaultText={selectedText}
            defaultIsNewTab={linkAttrs.target === '_blank'}
            onSave={onSetLink}
            className='w-full min-w-80 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none'
          />
        ) : (
          <LinkPopoverBlock
            onClear={onUnsetLink}
            url={linkAttrs.href}
            onEdit={handleEdit}
          />
        )}
      </div>
    </BubbleMenu>
  );
};
